/**
 * ClipboardSlots.ts - Multiple clipboard slots management
 * 
 * CHANGE HISTORY:
 * - 251102_1145 [pending] danielbeer: Created clipboard slots management
 * 
 * Manages named clipboard slots for different contexts and workflows.
 */

import type { ClipboardItem, ClipboardSlot } from '../types';

/**
 * Clipboard slots manager
 */
export class ClipboardSlots {
  private slots: Map<string, ClipboardSlot> = new Map();
  private defaultSlot: string = 'default';
  
  constructor(defaultSlot: string = 'default') {
    this.defaultSlot = defaultSlot;
    
    // Create default slot
    this.createSlot(defaultSlot);
  }
  
  /**
   * Create a new slot
   */
  createSlot(name: string, locked: boolean = false): ClipboardSlot {
    const slot: ClipboardSlot = {
      name,
      item: null,
      locked,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    this.slots.set(name, slot);
    return slot;
  }
  
  /**
   * Get slot by name
   */
  getSlot(name: string): ClipboardSlot | null {
    return this.slots.get(name) || null;
  }
  
  /**
   * Set item in slot
   */
  setItem(slotName: string, item: ClipboardItem): void {
    let slot = this.slots.get(slotName);
    
    // Create slot if it doesn't exist
    if (!slot) {
      slot = this.createSlot(slotName);
    }
    
    // Check if locked
    if (slot.locked) {
      throw new Error(`Slot "${slotName}" is locked`);
    }
    
    // Update slot
    slot.item = item;
    slot.updatedAt = Date.now();
  }
  
  /**
   * Get item from slot
   */
  getItem(slotName: string): ClipboardItem | null {
    const slot = this.slots.get(slotName);
    return slot?.item || null;
  }
  
  /**
   * Clear slot
   */
  clearSlot(slotName: string): boolean {
    const slot = this.slots.get(slotName);
    
    if (!slot) {
      return false;
    }
    
    if (slot.locked) {
      throw new Error(`Slot "${slotName}" is locked`);
    }
    
    slot.item = null;
    slot.updatedAt = Date.now();
    return true;
  }
  
  /**
   * Delete slot
   */
  deleteSlot(slotName: string): boolean {
    // Cannot delete default slot
    if (slotName === this.defaultSlot) {
      throw new Error('Cannot delete default slot');
    }
    
    const slot = this.slots.get(slotName);
    
    if (!slot) {
      return false;
    }
    
    if (slot.locked) {
      throw new Error(`Slot "${slotName}" is locked`);
    }
    
    return this.slots.delete(slotName);
  }
  
  /**
   * Lock slot
   */
  lockSlot(slotName: string): void {
    const slot = this.slots.get(slotName);
    if (slot) {
      slot.locked = true;
      slot.updatedAt = Date.now();
    }
  }
  
  /**
   * Unlock slot
   */
  unlockSlot(slotName: string): void {
    const slot = this.slots.get(slotName);
    if (slot) {
      slot.locked = false;
      slot.updatedAt = Date.now();
    }
  }
  
  /**
   * Check if slot exists
   */
  hasSlot(slotName: string): boolean {
    return this.slots.has(slotName);
  }
  
  /**
   * Check if slot has data
   */
  hasData(slotName: string): boolean {
    const slot = this.slots.get(slotName);
    return slot?.item !== null && slot?.item !== undefined;
  }
  
  /**
   * Get all slot names
   */
  getSlotNames(): string[] {
    return Array.from(this.slots.keys());
  }
  
  /**
   * Get all slots
   */
  getAllSlots(): ClipboardSlot[] {
    return Array.from(this.slots.values());
  }
  
  /**
   * Get active slots (with data)
   */
  getActiveSlots(): ClipboardSlot[] {
    return Array.from(this.slots.values()).filter(slot => slot.item !== null);
  }
  
  /**
   * Get empty slots
   */
  getEmptySlots(): ClipboardSlot[] {
    return Array.from(this.slots.values()).filter(slot => slot.item === null);
  }
  
  /**
   * Get locked slots
   */
  getLockedSlots(): ClipboardSlot[] {
    return Array.from(this.slots.values()).filter(slot => slot.locked);
  }
  
  /**
   * Clear all slots
   */
  clearAll(includeDefault: boolean = false): void {
    this.slots.forEach((slot, name) => {
      if (!slot.locked && (includeDefault || name !== this.defaultSlot)) {
        slot.item = null;
        slot.updatedAt = Date.now();
      }
    });
  }
  
  /**
   * Get default slot name
   */
  getDefaultSlot(): string {
    return this.defaultSlot;
  }
  
  /**
   * Set default slot
   */
  setDefaultSlot(slotName: string): void {
    // Create slot if it doesn't exist
    if (!this.slots.has(slotName)) {
      this.createSlot(slotName);
    }
    
    this.defaultSlot = slotName;
  }
  
  /**
   * Export slots
   */
  export(): Array<[string, ClipboardSlot]> {
    return Array.from(this.slots.entries());
  }
  
  /**
   * Import slots
   */
  import(data: Array<[string, ClipboardSlot]>): void {
    this.slots.clear();
    data.forEach(([name, slot]) => {
      this.slots.set(name, slot);
    });
  }
  
  /**
   * Get statistics
   */
  getStats() {
    const slots = Array.from(this.slots.values());
    
    return {
      totalSlots: slots.length,
      activeSlots: slots.filter(s => s.item !== null).length,
      emptySlots: slots.filter(s => s.item === null).length,
      lockedSlots: slots.filter(s => s.locked).length,
      defaultSlot: this.defaultSlot
    };
  }
}
